import type { ImageSizingProps, ImageUrlBuilderProps } from '@/components/image/types'

import { lcpImageQualities, lcpImageWidths } from './lcp-config'
import { defaultImageQuality } from './quality'

const youtubeDimensions: Record<string, [number, number]> = {
  maxresdefault: [1280, 720],
  sddefault: [640, 480],
  hqdefault: [480, 360],
  mqdefault: [320, 180],
  default: [120, 90],
}

// Legacy Storyblok assets omit the scheme. Resolve it before validation and
// URL construction so rendering and warming share the same HTTPS cache key.
const normalizeSourceUrl = (source: string): string => (source.startsWith('//') ? `https:${source}` : source)

export const lcpImageSource = (source: string) => {
  const url = normalizeSourceUrl(source)
  const storyblok = url.match(
    /^https:\/\/a\.storyblok\.com\/f\/45415\/(\d+)x(\d+)\/[^/]+\/[^/?#]+\.(?:jpe?g|png|webp|avif)$/i
  )
  if (storyblok && Number(storyblok[1]) > 0 && Number(storyblok[2]) > 0) {
    return { url, width: Number(storyblok[1]), height: Number(storyblok[2]) }
  }
  const youtube = url.match(
    /^https:\/\/i\.ytimg\.com\/vi(?:_webp)?\/[\w-]+\/(maxresdefault|sddefault|hqdefault|mqdefault|default)\.(?:jpg|webp)$/
  )
  if (youtube) {
    const [width, height] = youtubeDimensions[youtube[1]]
    return { url, width, height }
  }
  return undefined
}

export const lcpImageQuality = (quality = defaultImageQuality): number => {
  const parsedQuality = Number(quality)
  const requestedQuality =
    quality.trim() && Number.isFinite(parsedQuality) ? parsedQuality : Number(defaultImageQuality)
  return lcpImageQualities.find((value) => value >= requestedQuality) ?? 100
}

export const lcpImageUrl = ({ baseUrl, width, quality }: ImageUrlBuilderProps) => {
  // The optimizer does not enlarge originals. If the native width falls
  // between buckets, request the next bucket while retaining its real width
  // in srcset (e.g. an 1800px source requested with w=1920 stays 1800px).
  const allowedWidth = lcpImageWidths.find((size) => size >= width) ?? lcpImageWidths.at(-1)!
  return `/_next/image?url=${encodeURIComponent(normalizeSourceUrl(baseUrl))}&w=${allowedWidth}&q=${lcpImageQuality(quality)}`
}

export const lcpImageSizing = (source: string, sizing: ImageSizingProps = {}): ImageSizingProps | undefined => {
  const asset = lcpImageSource(source)
  if (!asset || sizing.fixed) {
    return undefined
  }
  return {
    ...sizing,
    nativeWidth: asset.width,
    nativeHeight: asset.height,
    maxWidth: Math.min(sizing.maxWidth ?? 2268, asset.width, 2492),
    widths: lcpImageWidths,
    quality: sizing.quality || defaultImageQuality,
    toUrl: lcpImageUrl,
  }
}
